import { useEffect, useState } from "react";
import { SafeImage } from "@/components/site/SafeImage";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Calendar, User } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Contact";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setNotFound(false);
    api
      .get(`/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = r.data.seo_title || `${r.data.title} | LaunchLab`;
        if (r.data.seo_description) {
          document.querySelector('meta[name="description"]')?.setAttribute("content", r.data.seo_description);
        }
        api.get("/posts").then((res) => {
          setRelated(res.data.filter((p) => p.category === r.data.category && p.id !== r.data.id).slice(0, 3));
        });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  return (
    <div data-testid="blog-post-page" className="relative w-full max-w-[100vw] overflow-x-clip text-slate-100 min-h-screen">
      <Header />
      <div className="pt-32 pb-24 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#FF8800] transition-colors mb-10">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        {notFound ? (
          <div className="glass rounded-2xl p-14 text-center text-slate-400" data-testid="blog-post-not-found">Article not found.</div>
        ) : !post ? (
          <div className="flex justify-center py-20"><div className="w-10 h-10 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
        ) : (
          <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="glass px-3.5 py-1.5 rounded-full font-mono-tech text-[10px] tracking-[0.18em] text-[#00B0FF] uppercase">
              {post.category}
            </span>
            <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight" data-testid="blog-post-title">
              {post.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-slate-500">
              <span className="flex items-center gap-2"><User className="w-4 h-4" /> {post.author}</span>
              {post.created_at && (
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              )}
            </div>
            {post.image && (
              <SafeImage
                src={post.image}
                alt={post.title}
                className="mt-8 block w-full aspect-[1200/630] object-cover rounded-2xl border border-slate-800"
                fallback={null}
              />
            )}
            <div className="mt-10 space-y-6" data-testid="blog-post-content">
              {(post.content || "").split(/\n\s*\n/).filter(Boolean).map((para, i) => (
                <p key={i} className="text-base sm:text-lg text-slate-300 leading-relaxed">{para}</p>
              ))}
            </div>
            {post.tags?.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2" data-testid="blog-post-tags">
                {post.tags.map((t) => (
                  <span key={t} className="glass px-3 py-1.5 rounded-full text-xs text-slate-400">#{t}</span>
                ))}
              </div>
            )}
          </motion.article>
        )}

        {related.length > 0 && (
          <div className="mt-20" data-testid="blog-related">
            <h2 className="font-display text-2xl font-bold text-white mb-8">Related Articles</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-related-${p.slug}`}
                  className="group glass rounded-xl p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#FF8800]/40"
                >
                  <span className="font-mono-tech text-[9px] tracking-[0.2em] text-[#00B0FF] uppercase">{p.category}</span>
                  <p className="mt-2 font-display font-semibold text-sm text-white leading-snug group-hover:text-[#FF8800] transition-colors">{p.title}</p>
                  <ArrowUpRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-[#FF8800] mt-3 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
